// Every way a sizing gets INTO the v3 calculator, funnelled through the v2 -> v3 migration
// (engine/migrate-v3.ts), plus the one way it is parked for a hard reload. Pure: each loader takes
// raw data and returns what to hydrate the store with (or null when the data is unusable), so the
// wiring is testable without a store, a DOM or a network. `applyLoaded` is the only side effect.
//
//   share link     `?s=<base64>`            migrateSharePayload
//   session        `clay-calc-session-v1`   migrateSession (persisted as v3, reads v2 envelopes too)
//   config import  saved-settings markdown  migrateConfigImport
//   Salesforce     Story_JSON__c            migrateSavedStory (v2-calculator v1, or v3 story v2)
//   deck snapshot  `clay-value-story-v1`    migrateDeckSnapshot, owned by the /story route
//
// A load never writes: hydrating re-anchors autosave's baseline, so opening something is not an edit.

import {
  migrateConfigImport,
  migrateSavedStory,
  migrateSession,
  migrateSharePayload,
  type LinkedAccountShape,
  type MigratedSizing,
  type Recheck,
  type SizingState,
} from '../engine/migrate-v3';
import type { CalculatorInput } from '../engine/use-case-engine';
import { decodeShareRaw, encodeShareV3, v3Payload } from '../engine/sizing-transfer';
import { ACCOUNT_STEP, SELECTION_STEP, type AccountSelection } from '../flow/step-model';
import {
  calculatorActions,
  sizingOf,
  stepsOf,
  useCalculatorStore,
  type CalculatorState,
  type HydrateMeta,
} from './calculator-store';

export const SESSION_KEY = 'clay-calc-session-v1';

export interface LoadedSizing {
  sizing: CalculatorInput;
  /** Open migration flags, shown in the review list. */
  recheck: Recheck[];
  /** The customer name the data carried (the account name wins once an account is linked). */
  customerName: string;
  migratedFrom: 'v2' | null;
  meta: HydrateMeta;
}

/** The sizing slice of a migrated payload, in engine input shape. */
export function inputOf(s: SizingState): CalculatorInput {
  return { deal: s.deal, selected: s.selected, tools: s.tools, customTools: s.customTools };
}

/**
 * Where a loaded sizing lands: the use case selection when an account or demo mode is already
 * chosen, otherwise the account step (every later step is locked until one is).
 */
export function landingStep(account: AccountSelection | null): string {
  return account ? SELECTION_STEP : ACCOUNT_STEP;
}

function loaded(m: MigratedSizing, account: AccountSelection | null, step?: string): LoadedSizing {
  return {
    sizing: inputOf(m.sizing),
    recheck: m.recheck,
    customerName: m.sizing.customerName,
    migratedFrom: m.migratedFrom,
    meta: { account, path: 'business-case', step: step ?? landingStep(account), recheck: m.recheck },
  };
}

/* ----------------------------------------------------------------------- share link */

/** `?s=` value -> sizing. `account` is whatever is already chosen (a link carries none). */
export function loadFromShare(param: string, account: AccountSelection | null): LoadedSizing | null {
  const raw = decodeShareRaw(param);
  if (raw === null) return null;
  const m = migrateSharePayload(raw);
  return m ? loaded(m, account) : null;
}

/** The sizing as a share link query value (a v3 payload). */
export function shareParamFor(state: CalculatorState, customerName: string): string | null {
  return encodeShareV3(v3Payload(sizingStateOf(state, customerName), state.recheck));
}

/** Store state -> the migrate-v3 sizing shape (adds the customer name the payload carries). */
export function sizingStateOf(state: CalculatorState, customerName: string): SizingState {
  const sizing = sizingOf(state);
  return {
    customerName,
    deal: sizing.deal,
    selected: sizing.selected.map((s) => ({ ...s })),
    tools: sizing.tools ?? null,
    customTools: (sizing.customTools ?? []).map((t) => ({ name: t.name, spend: t.spend })),
  };
}

export function customerNameOf(account: AccountSelection | null): string {
  return account?.kind === 'account' ? account.accountName : '';
}

/* ----------------------------------------------------------------------- session */

function accountFromLinked(l: LinkedAccountShape): AccountSelection {
  return {
    kind: 'account',
    accountId: l.id,
    accountName: l.name,
    ...(l.opportunityId ? { opportunityId: l.opportunityId, opportunityName: l.opportunityName } : {}),
  };
}

function linkedFromAccount(a: AccountSelection | null): LinkedAccountShape | null {
  if (a?.kind !== 'account') return null;
  return {
    id: a.accountId,
    name: a.accountName,
    ...(a.opportunityId ? { opportunityId: a.opportunityId, opportunityName: a.opportunityName } : {}),
  };
}

/**
 * What to park in sessionStorage. A v3 payload inside the same `{ share, linkedAccount, demoMode }`
 * envelope v2 used, plus the current step. Null when there is nothing worth restoring (the homepage).
 */
export function serializeSession(state: CalculatorState): Record<string, unknown> | null {
  if (state.path !== 'business-case') return null;
  return {
    share: v3Payload(sizingStateOf(state, customerNameOf(state.account)), state.recheck),
    linkedAccount: linkedFromAccount(state.account),
    demoMode: state.account?.kind === 'demo',
    step: state.currentStepId,
  };
}

/** Parked session -> sizing, account and step. Reads a v3 session or any v2 envelope. */
export function loadFromSession(raw: unknown): LoadedSizing | null {
  const s = migrateSession(raw);
  if (!s) return null;
  const account: AccountSelection | null = s.linkedAccount
    ? accountFromLinked(s.linkedAccount)
    : s.demoMode
      ? { kind: 'demo' }
      : null;
  const parkedStep = (raw as { step?: unknown } | null)?.step;
  return loaded(s.migrated, account, typeof parkedStep === 'string' ? parkedStep : undefined);
}

/* ----------------------------------------------------------------- config import */

export function loadFromConfig(markdown: string, account: AccountSelection | null): LoadedSizing | null {
  const m = migrateConfigImport(markdown);
  return m ? loaded(m, account) : null;
}

/* ------------------------------------------------------------------- Salesforce */

/**
 * A saved Story_JSON__c -> sizing. The account stays as chosen; the step stays where the SE is,
 * since this runs while they are on the account step.
 */
export function loadFromSavedStory(raw: unknown, account: AccountSelection, step: string = ACCOUNT_STEP): LoadedSizing | null {
  const m = migrateSavedStory(raw);
  return m ? loaded(m, account, step) : null;
}

/* --------------------------------------------------------------------- apply */

/**
 * Replace the store's sizing with a loaded one. The only side effect in this module. A parked step
 * that no longer exists or is locked (a use case since dropped, no account chosen) falls back to
 * the landing step.
 */
export function applyLoaded(l: LoadedSizing): void {
  calculatorActions.hydrate(l.sizing, l.meta);
  const state = useCalculatorStore.getState();
  const step = stepsOf(state).find((x) => x.id === state.currentStepId);
  if (!step || step.locked) calculatorActions.goToStep(landingStep(state.account));
}
